'use client'

import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { LaunchpadSearch } from '@/lib/notebook/editor/launcher/LaunchpadSearch'
import { motion } from 'framer-motion'
import { NB_EASE, NB_SPRING_SOFT } from '@/lib/notebook/core/notebook-motion-presets'
import {
  CATEGORIES,
  LAUNCHER_ENTRIES,
  registryEntry,
} from '@/lib/notebook/registry/sections-registry'
import { BlockLaunchpadPreview } from '@/lib/notebook/editor/launcher/BlockLaunchpadPreview'
import {
  getLaunchpadVisual,
  LAUNCHPAD_CATEGORY_ACCENTS,
} from '@/lib/notebook/editor/launcher/block-launchpad-visuals'

const gridVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.03, delayChildren: 0.05 },
  },
}

const tileVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { ...NB_SPRING_SOFT, opacity: { duration: 0.22, ease: NB_EASE } },
  },
}

/**
 * Launchpad-style block explorer — presentational; parent wires addSection + onClose.
 */
export function SectionExplorer({ onClose, onAdd, entries = LAUNCHER_ENTRIES }) {
  const [active, setActive] = useState('all')
  const [query, setQuery] = useState('')

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const filtered = useMemo(() => {
    return entries.filter((entry) => {
      if (active !== 'all' && entry.category !== active) return false
      if (!query.trim()) return true
      const q = query.toLowerCase()
      return (
        entry.label.toLowerCase().includes(q) ||
        entry.desc.toLowerCase().includes(q)
      )
    })
  }, [active, query, entries])

  const handlePick = (entry) => {
    if (entry.comingSoon) return
    onAdd(entry.type)
  }

  return (
    <motion.div
      className="nb-launchpad-backdrop"
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: NB_EASE }}
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="nb-section-explorer-title"
        className="nb-launchpad"
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 6 }}
        transition={{ ...NB_SPRING_SOFT, opacity: { duration: 0.24, ease: NB_EASE } }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="nb-launchpad-header">
          <div className="nb-launchpad-header__titles">
            <h2 id="nb-section-explorer-title">Add a section</h2>
            <p>Compose your notebook from modular pieces.</p>
          </div>
          <button
            type="button"
            className="nb-launchpad-close"
            onClick={onClose}
            aria-label="Close section explorer"
          >
            <X size={17} strokeWidth={2} />
          </button>
        </header>

        <LaunchpadSearch onValueChange={setQuery} />

        <div className="nb-launchpad-filters" role="tablist" aria-label="Block categories">
          <button
            type="button"
            role="tab"
            aria-selected={active === 'all'}
            className={`nb-launchpad-pill${active === 'all' ? ' is-active' : ''}`}
            style={{ '--pill-hue': '220' }}
            onClick={() => setActive('all')}
          >
            All
          </button>
          {CATEGORIES.map((cat) => {
            const accent = LAUNCHPAD_CATEGORY_ACCENTS[cat.id]
            return (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={active === cat.id}
                className={`nb-launchpad-pill${active === cat.id ? ' is-active' : ''}`}
                style={{ '--pill-hue': String(accent?.hue ?? 220) }}
                onClick={() => setActive(cat.id)}
              >
                {cat.label}
              </button>
            )
          })}
        </div>

        <div className="nb-launchpad-scroll">
          <motion.div
            className="nb-launchpad-grid"
            variants={gridVariants}
            initial="hidden"
            animate="show"
            key={`${active}-${query}`}
          >
          {filtered.map((entry) => {
            const meta = registryEntry(entry.type)
            const visual = getLaunchpadVisual(entry.type)
            const categoryHue =
              LAUNCHPAD_CATEGORY_ACCENTS[entry.category]?.hue ?? visual.hue

            return (
              <motion.button
                key={entry.type}
                type="button"
                variants={tileVariants}
                disabled={entry.comingSoon}
                className="nb-launchpad-tile"
                style={{ '--tile-hue': String(categoryHue) }}
                onClick={() => handlePick(entry)}
                whileHover={entry.comingSoon ? undefined : { y: -2 }}
                whileTap={entry.comingSoon ? undefined : { y: 0 }}
                aria-label={
                  entry.comingSoon
                    ? `${entry.label} — coming soon`
                    : `Add ${entry.label}`
                }
              >
                {entry.comingSoon && (
                  <span className="nb-launchpad-soon">Soon</span>
                )}
                <BlockLaunchpadPreview
                  variant={visual.preview}
                  hue={visual.hue}
                />
                <span className="nb-launchpad-tile__label">
                  {entry.label || meta?.label}
                </span>
                <span className="nb-launchpad-tile__desc">{entry.desc}</span>
              </motion.button>
            )
          })}
          {filtered.length === 0 && (
            <p className="nb-launchpad-empty" role="status">
              No blocks matched &ldquo;{query}&rdquo;
            </p>
          )}
          </motion.div>
        </div>

        <footer className="nb-launchpad-footer">
          <span>
            Press <kbd className="nb-kbd">esc</kbd> to close
          </span>
          <span>{LAUNCHER_ENTRIES.length} sections</span>
        </footer>
      </motion.div>
    </motion.div>
  )
}
